import {Action, Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  Section,
  UploadDocumentsDialog,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {TestIdProps, buildArray, suffixTestId} from 'shared/web';

import type {EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

interface DocumentsProps extends TestIdProps {
  maximization?: EagGridMaximization;
  onDocumentAction: VoidFunction;
}

export function Documents(props: DocumentsProps) {
  const {directSaleId} = useWarehouseParams();

  const [directSaleEdit] = usePermissions({
    permissionKeys: ['directSaleEdit'],
  });

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const [rowActions, bulkSignatureAction] = useGetDocumentDatagridActions({
    contextId: directSaleId,
    contextTarget: 'direct-sale',
    refreshData: () => refreshDmsDatagrid(DOCUMENTS_GRID_CODE),
  });

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {directSaleId}]),
    [directSaleId]
  );

  const documentsActions = buildArray<Action>([
    {
      type: 'button',
      variant: 'ghostLink',
      leftIcon: 'action/backup',
      title: i18n.t('entity.document.actions.uploadDocument'),
      onClick: openUploadDialog,
      isDisabled: !directSaleEdit,
      tooltip: !directSaleEdit
        ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
        : undefined,
      'data-testid': suffixTestId('action.addDocument', props),
    },
  ]).when(directSaleEdit, ...bulkSignatureAction);

  return (
    <Section isFullHeight data-testid={suffixTestId('wrapper', props)}>
      <Card
        isFullHeight
        title={i18n.t('page.warehouse.labels.documents')}
        actions={documentsActions}
      >
        <Box height="100%">
          <DmsDataGrid
            maximization={props.maximization}
            gridCode={DOCUMENTS_GRID_CODE}
            emptyState={{headline: i18n.t('general.labels.noData')}}
            actionCallback={rowActions}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            data-testid={DOCUMENTS_GRID_CODE}
          />
        </Box>
      </Card>

      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={() => {
          refreshDmsDatagrid(DOCUMENTS_GRID_CODE);
          props.onDocumentAction();
        }}
        contextTarget="direct-sale"
        contextId={directSaleId}
        bulkContext={[{target: 'direct-sale', targetId: directSaleId}]}
        data-testid={suffixTestId('upload-document', props)}
      />
    </Section>
  );
}

const DOCUMENTS_GRID_CODE = 'document-context-list-direct-sale';
